import React, {
  createContext,
  useState,
  useContext,
  useEffect
} from 'react';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const API_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const storedToken = localStorage.getItem('marilink_token');
    const storedUser = localStorage.getItem('marilink_user');

    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
    }

    setLoading(false);
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.success) {
        setToken(data.token);
        setUser(data.user);

        localStorage.setItem('marilink_token', data.token);
        localStorage.setItem(
          'marilink_user',
          JSON.stringify(data.user)
        );

        return {
          success: true,
          user: data.user,
          role: data.user.role
        };
      }

      return {
        success: false,
        message: data.message || 'Invalid credentials',
        needsVerification: data.needsVerification || false,
        email: data.email
      };
    } catch (err) {
      return {
        success: false,
        message: 'Network error. Make sure backend is running.'
      };
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async (credential) => {
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/google-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ credential })
      });

      const data = await response.json();

      if (data.success) {
        setToken(data.token);
        setUser(data.user);

        localStorage.setItem('marilink_token', data.token);
        localStorage.setItem(
          'marilink_user',
          JSON.stringify(data.user)
        );

        return {
          success: true,
          user: data.user,
          needsProfileCompletion:
            data.needsProfileCompletion || false
        };
      }

      return {
        success: false,
        message: data.message || 'Google login failed',
        code: data.code || null
      };
    } catch (err) {
      return {
        success: false,
        message: 'Network error. Please try again.'
      };
    } finally {
      setLoading(false);
    }
  };

  const register = async (formData) => {
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/register`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        return {
          success: true,
          user: data.user,
          role: data.user?.role,
          email: data.email,
          needsVerification: data.needsVerification !== false,
          token: data.token
        };
      }

      return {
        success: false,
        message: data.message || 'Registration failed'
      };
    } catch (err) {
      return {
        success: false,
        message: 'Network error. Please try again.'
      };
    } finally {
      setLoading(false);
    }
  };

  const completeProfile = async (profileData) => {
    setError(null);

    try {
      const currentToken =
        token || localStorage.getItem('marilink_token');

      if (!currentToken) {
        throw new Error(
          'Your session has expired. Please sign in again.'
        );
      }

      const body =
        profileData instanceof FormData
          ? profileData
          : new FormData();

      if (!(profileData instanceof FormData)) {
        Object.entries(profileData || {}).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            return;
          }

          body.append(
            key,
            Array.isArray(value) ? JSON.stringify(value) : value
          );
        });
      }

      const selectedRole = body.get('role');

      if (!['owner', 'service-provider'].includes(selectedRole)) {
        throw new Error(
          'Please select Ship Owner or Service Provider.'
        );
      }

      const response = await fetch(
        `${API_URL}/users/complete-profile`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${currentToken}`
          },
          credentials: 'include',
          body
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok || data?.success !== true || !data.user) {
        throw new Error(
          data?.message || 'Failed to complete your profile.'
        );
      }

      if (data.user.role !== selectedRole) {
        throw new Error(
          'The server did not save your selected account type. Please contact support.'
        );
      }

      if (data.user.profileCompleted !== true) {
        throw new Error(
          'The server did not complete your profile. Please try again.'
        );
      }

      let storedUser = null;

      try {
        storedUser = JSON.parse(
          localStorage.getItem('marilink_user')
        );
      } catch {
        storedUser = null;
      }

      const returnedId = String(
        data.user.id || data.user._id || ''
      );

      const candidate = user || storedUser;

      const sameAccount =
        returnedId &&
        candidate &&
        String(candidate.id || candidate._id || '') === returnedId;

      const updatedUser = {
        ...(sameAccount ? candidate : {}),
        ...data.user
      };

      localStorage.setItem('marilink_token', currentToken);
      localStorage.setItem(
        'marilink_user',
        JSON.stringify(updatedUser)
      );

      setToken(currentToken);
      setUser(updatedUser);

      return {
        success: true,
        user: updatedUser
      };
    } catch (err) {
      setError(err.message);

      return {
        success: false,
        message: err.message
      };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (e) {
      console.warn('Logout endpoint error:', e);
    }

    setUser(null);
    setToken(null);

    localStorage.removeItem('marilink_user');
    localStorage.removeItem('marilink_token');
  };

  const updateProfile = async (profileData) => {
    setLoading(true);

    try {
      const currentToken =
        token || localStorage.getItem('marilink_token');

      const response = await fetch(`${API_URL}/users/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`
        },
        credentials: 'include',
        body: JSON.stringify(profileData)
      });

      const data = await response.json();

      if (data.success) {
        const updatedUser = {
          ...user,
          ...data.user
        };

        setUser(updatedUser);

        localStorage.setItem(
          'marilink_user',
          JSON.stringify(updatedUser)
        );

        return {
          success: true,
          user: updatedUser
        };
      }

      return {
        success: false,
        message: data.message
      };
    } catch (err) {
      return {
        success: false,
        message: err.message
      };
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    setLoading(true);

    try {
      const currentToken =
        token || localStorage.getItem('marilink_token');

      const response = await fetch(
        `${API_URL}/auth/change-password`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${currentToken}`
          },
          credentials: 'include',
          body: JSON.stringify({
            currentPassword,
            newPassword
          })
        }
      );

      const data = await response.json();

      return {
        success: data.success,
        message: data.message
      };
    } catch (err) {
      return {
        success: false,
        message: err.message
      };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated: !!user,
    isOwner: user?.role === 'owner',
    isServiceProvider: user?.role === 'service-provider',
    isAdmin: user?.role === 'admin',
    login,
    googleLogin,
    register,
    completeProfile,
    logout,
    updateProfile,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};